import React from 'react';

import { ProductDetails } from 'types/state';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { formatDate } from 'utils/formatDate';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { MarginComponent } from 'design/margin';
import { TextComponent } from 'design/text/textComponents';
import {
  SmallTableRowComponent,
  SmallTableWrapperComponent,
} from 'design/smallTable';
import { ModalPressControlComponent } from 'design/modalPressControl';

type Props = {
  details: ProductDetails;
  onEditProduct(): void;
  onEditTariff(): void;
  onCreateTariff(): void;
  onDeleteTariff(tariffId: number): void;
};

export const ProductManagerSummary = (props: Props) => {
  const {
    details,
    onEditProduct,
    onEditTariff,
    onCreateTariff,
    onDeleteTariff,
  } = props;

  const { product } = details;

  const { latestTariff, activeTariff } = product;

  const {
    productDetails: intl,
    shared: sharedDictionary,
  } = useIntlDictionary();

  const formatDateValue = React.useCallback(
    (ISODate: string) => {
      return sharedDictionary.dates.calendarDateWithTime(new Date(ISODate));
    },
    [sharedDictionary.dates]
  );

  const createDurationText = React.useCallback(
    (ISODateStartsAt: string, ISODateExpiresAt: string) => {
      const startsAt = formatDate(ISODateStartsAt, 'DD.MM.YY');

      const expiresAt = formatDate(ISODateExpiresAt, 'DD.MM.YY');

      return intl.table.durationValue(startsAt, expiresAt);
    },
    [intl.table]
  );

  const deleteLatestTariff = React.useCallback(() => {
    if (latestTariff) {
      onDeleteTariff(latestTariff.id);
    }
  }, [latestTariff, onDeleteTariff]);

  const hasActiveTariffBlock = !!activeTariff;

  const hasActiveTariffFallback = !activeTariff && !latestTariff;

  const activeTariffEqualsLatest =
    !!activeTariff && !!latestTariff && activeTariff.id === latestTariff.id;

  const hasLatestTariffBlock = !!latestTariff && !activeTariffEqualsLatest;

  return (
    <>
      <SidebarModalSectionComponent
        content={
          <>
            <TextComponent size="mp" weight="medium">
              {intl.table.descriptionTitle}
            </TextComponent>
            <MarginComponent top={20} />
            <SmallTableWrapperComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.nameLabel}</TextComponent>
                <TextComponent>{product.name}</TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.partnerPercentLabel}</TextComponent>
                <TextComponent>{product.defaultPartnerComission}</TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.createdAtLabel}</TextComponent>
                <TextComponent>
                  {product.createdAt ? formatDateValue(product.createdAt) : '-'}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.idLabel}</TextComponent>
                <TextComponent>{product.id}</TextComponent>
              </SmallTableRowComponent>
            </SmallTableWrapperComponent>
          </>
        }
        actions={
          <>
            <ModalPressControlComponent
              type="button"
              theme="secondary"
              onPress={onEditProduct}
            >
              {intl.buttons.editProduct}
            </ModalPressControlComponent>
          </>
        }
      />
      {hasActiveTariffFallback && (
        <SidebarModalSectionComponent
          content={
            <>
              <TextComponent size="mp" weight="medium">
                {intl.table.activeTariffTitle}
              </TextComponent>
              <MarginComponent top={20} />
              <TextComponent>{intl.table.missingTariffSubTitle}</TextComponent>
            </>
          }
          actions={
            <>
              <ModalPressControlComponent
                type="button"
                theme="secondary"
                onPress={onCreateTariff}
              >
                {intl.buttons.createTariff}
              </ModalPressControlComponent>
            </>
          }
        />
      )}
      {hasActiveTariffBlock && activeTariff !== null && (
        <SidebarModalSectionComponent
          content={
            <>
              <TextComponent size="mp" weight="medium">
                {intl.table.activeTariffTitle}
              </TextComponent>
              <MarginComponent top={20} />
              <SmallTableWrapperComponent>
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.costLabel}</TextComponent>
                  <TextComponent>{activeTariff.cost}</TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.durationLabel}</TextComponent>
                  <TextComponent>
                    {createDurationText(
                      activeTariff.startsAt,
                      activeTariff.expiresAt
                    )}
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.idLabel}</TextComponent>
                  <TextComponent>{activeTariff.id}</TextComponent>
                </SmallTableRowComponent>
              </SmallTableWrapperComponent>
            </>
          }
          actions={
            activeTariffEqualsLatest && (
              <>
                <ModalPressControlComponent
                  type="button"
                  theme="secondary"
                  onPress={onEditTariff}
                >
                  {intl.buttons.editTariff}
                </ModalPressControlComponent>
                <MarginComponent top={20} />
                <ModalPressControlComponent
                  type="button"
                  theme="secondary"
                  onPress={onCreateTariff}
                >
                  {intl.buttons.createTariff}
                </ModalPressControlComponent>
              </>
            )
          }
        />
      )}
      {hasLatestTariffBlock && latestTariff !== null && (
        <SidebarModalSectionComponent
          content={
            <>
              <TextComponent size="mp" weight="medium">
                {intl.table.latestTariffTitle}
              </TextComponent>
              <MarginComponent top={20} />
              <SmallTableWrapperComponent>
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.costLabel}</TextComponent>
                  <TextComponent>{latestTariff.cost}</TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.durationLabel}</TextComponent>
                  <TextComponent>
                    {createDurationText(
                      latestTariff.startsAt,
                      latestTariff.expiresAt
                    )}
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.idLabel}</TextComponent>
                  <TextComponent>{latestTariff.id}</TextComponent>
                </SmallTableRowComponent>
              </SmallTableWrapperComponent>
            </>
          }
          actions={
            <>
              <ModalPressControlComponent
                type="button"
                theme="secondary"
                onPress={onEditTariff}
              >
                {intl.buttons.editTariff}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                type="button"
                theme="secondary"
                onPress={deleteLatestTariff}
              >
                {intl.buttons.deleteTariff}
              </ModalPressControlComponent>
            </>
          }
        />
      )}
    </>
  );
};
